/**
 * The numbers behind the picture, leg by leg.
 *
 * The chart answers "does it clear"; this answers "by how much, and where". Both
 * are needed and neither replaces the other — a shape cannot be read off a
 * screenshot into a kneeboard, and a table cannot show that the ridge is in the
 * middle of leg 3 rather than at its end.
 *
 * The one action here is `Sicherheitshöhe übernehmen`, and it appears on exactly
 * the legs where it means something: the ones whose cell the PILOT owns and
 * whose value differs from the current suggestion. Everywhere else the cell is
 * already the suggestion — `terrain.ts` writes it unattended, as it has since
 * the column started printing empty on every plan whose pilot never found the
 * button. Taking a cell back from a pilot without being asked is the one thing
 * that does not happen.
 */

import { useT } from '../../i18n';
import { feetText, groupThousands, signedFeet } from './format';
import type { ProfileLeg } from './profile';

/** The obstacle kinds openAIP publishes that have a word of their own. */
const OBSTACLE_KINDS: readonly string[] = ['chimney', 'building', 'wind_turbine', 'tower'];

export interface LegRow {
  leg: number;
  from: string;
  to: string;
  p: ProfileLeg;
  /** the profile's own suggestion, ft — may differ from the cell the pilot owns */
  msaFt: number | null;
  /** `flat` | `mountain` | '' — which clearance the suggestion applied */
  msaRule: string;
  /** what the suggestion looks like in the form's own unit (`2.5`) */
  cell: string;
  /** the Sicherheitshöhe cell of this leg is the pilot's, not the profile's */
  mine: boolean;
  /** the form has a row for this leg (it has twelve) */
  writable: boolean;
}

/** The legs whose Sicherheitshöhe the pilot owns and whose value is out of date. */
export const takeableRows = (rows: readonly LegRow[]): { leg: number; cell: string }[] =>
  rows
    .filter(r => r.writable && r.mine && r.cell !== '' && r.p.suggestedFt !== r.msaFt)
    .map(r => ({ leg: r.leg, cell: r.cell }));

export function LegTerrainList({
  rows,
  onTake,
  onTakeAll,
}: {
  rows: readonly LegRow[];
  onTake(leg: number, cell: string): void;
  onTakeAll(list: readonly { leg: number; cell: string }[]): void;
}) {
  const t = useT();
  if (rows.length === 0) return null;
  const takeable = takeableRows(rows);

  return (
    <>
      <ul className="aet-legs">
        {rows.map(r => {
          const { p } = r;
          const obst = p.obstacle;
          const obstName = obst
            ? obst.name || (OBSTACLE_KINDS.includes(obst.kind) ? t('obst.kind.' + obst.kind) : '')
            : '';
          const obstKey = obstName ? 'terrain.obst' : 'terrain.obst.plain';
          const canTake = takeable.some(x => x.leg === r.leg);

          return (
            <li key={r.leg} className={'aet-leg aet-' + p.level}>
              <div className="aet-leg-head">
                <span className="aet-leg-n ae-num">{r.leg}</span>
                <span className="aet-leg-name">{r.from} → {r.to}</span>
                {p.level === 'ok' ? null : (
                  <span className={'aet-flag aet-' + p.level}>
                    {p.level === 'none' ? t('terrain.flag.none') : t('terrain.flag.low')}
                  </span>
                )}
              </div>

              <div className="aet-leg-facts">
                {p.maxTerrainFt == null ? (
                  <span>{t('terrain.nodata')}</span>
                ) : (
                  <span>
                    {p.maxTerrainAtNm == null
                      ? t('terrain.max.plain', { ft: groupThousands(p.maxTerrainFt) })
                      : t('terrain.max', {
                          ft: groupThousands(p.maxTerrainFt),
                          nm: p.maxTerrainAtNm,
                        })}
                  </span>
                )}
                {obst ? (
                  <span>
                    {t(obst.exact ? obstKey : obstKey + '.min', {
                      name: obstName,
                      ft: groupThousands(obst.ft),
                    })}
                  </span>
                ) : null}
              </div>

              <div className="aet-leg-nums">
                <span className="aet-num-slot">
                  <span className="ae-eyebrow">{t('terrain.col.suggest')}</span>
                  <span className={'ae-num' + (r.mine ? '' : ' ae-derived')}>
                    {p.suggestedFt == null ? t('terrain.none.short') : feetText(p.suggestedFt)}
                  </span>
                  {r.msaRule ? <span className="aet-rule">{t('terrain.rule.' + r.msaRule)}</span> : null}
                </span>

                <span className="aet-num-slot">
                  <span className="ae-eyebrow">{t('terrain.col.planned')}</span>
                  <span className="ae-num">
                    {p.plannedFt == null ? t('terrain.planned.none') : feetText(p.plannedFt)}
                  </span>
                  {p.clearanceFt === null ? null : (
                    <span className={'aet-gap aet-' + p.level}>{signedFeet(p.clearanceFt)}</span>
                  )}
                </span>

                {canTake ? (
                  <button
                    type="button"
                    className="ae-btn ae-btn-quiet aet-take"
                    title={t('terrain.take.leg', { v: r.cell, n: r.leg })}
                    onClick={() => onTake(r.leg, r.cell)}
                  >
                    {t('terrain.take')}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {takeable.length > 1 ? (
        <button
          type="button"
          className="ae-btn ae-btn-quiet aet-take-all"
          onClick={() => onTakeAll(takeable)}
        >
          {t('terrain.take.all', { n: takeable.length })}
        </button>
      ) : null}
    </>
  );
}
